import { useState, useEffect, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, isColorDark, resolveLinkProps, resolveImage } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionIcon from './SectionIcon';

export default function FeatureCarouselSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [sectionRef, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'split';
    const items = content.items || [];
    const [currentSlide, setCurrentSlide] = useState(0);

    const total = items.length;
    const next = useCallback(() => setCurrentSlide(p => (p + 1) % total), [total]);
    const prev = useCallback(() => setCurrentSlide(p => (p - 1 + total) % total), [total]);

    // Autoplay
    useEffect(() => {
        if (!config.autoplay || total <= 1) return;
        const ms = (config.interval || 5) * 1000;
        const timer = setInterval(next, ms);
        return () => clearInterval(timer);
    }, [config.autoplay, config.interval, total, next]);

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const accentDark = isColorDark(s.accent);

    // Checkmark icon for feature bullets
    const Check = () => (
        <svg className="w-4 h-4 shrink-0 mt-0.5" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
        </svg>
    );

    // Navigation arrows
    const ArrowBtn = ({ direction, onClick, className = '' }) => (
        <button onClick={onClick}
            className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 hover:scale-110 ${className}`}
            style={{ borderColor: hexToRgba(s.accent, 0.3), color: s.textSub, backgroundColor: 'transparent' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = s.accent; e.currentTarget.style.color = accentDark ? '#ffffff' : '#111827'; e.currentTarget.style.borderColor = s.accent; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = s.textSub; e.currentTarget.style.borderColor = hexToRgba(s.accent, 0.3); }}>
            {direction === 'prev' ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
            ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            )}
        </button>
    );

    // Slide indicator dots
    const Dots = ({ className = '' }) => total > 1 && (
        <div className={`flex items-center gap-2 ${className}`}>
            {items.map((_, i) => (
                <button key={i} onClick={() => setCurrentSlide(i)}
                    className="h-2 rounded-full transition-all duration-300"
                    style={{ width: i === currentSlide ? 32 : 8, backgroundColor: i === currentSlide ? s.accent : hexToRgba(s.accent, 0.25) }}/>
            ))}
        </div>
    );

    // Feature bullet list
    const FeatureList = ({ features = [], limit }) => {
        const list = limit ? features.slice(0, limit) : features;
        if (list.length === 0) return null;
        return (
            <ul className="space-y-2 mt-4">
                {list.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-relaxed" style={{ color: s.textSub }}>
                        <Check/> {feat}
                    </li>
                ))}
            </ul>
        );
    };

    // CTA button for an item
    const CtaButton = ({ item }) => {
        if (!item.cta_text) return null;
        return (
            <a {...resolveLinkProps(item.cta_link || '#')}
                className="inline-flex items-center gap-2 text-sm font-semibold mt-5 px-5 py-2.5 rounded-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: s.accent, color: accentDark ? '#ffffff' : '#111827' }}>
                {item.cta_text}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            </a>
        );
    };

    // Slide image
    const SlideImage = ({ item, className = '', aspect = 'aspect-[16/10]' }) => {
        const img = resolveImage(item, 'image', mediaFolders);
        if (!img) return (
            <div className={`${aspect} ${className} rounded-xl flex items-center justify-center`} style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                <svg className="w-16 h-16" style={{ color: hexToRgba(s.accent, 0.3) }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <rect x="2" y="2" width="20" height="20" rx="2" strokeWidth={1.5}/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2 15l5-5 3 3 4-4 8 8"/>
                    <circle cx="15.5" cy="7.5" r="1.5" strokeWidth={1.5}/>
                </svg>
            </div>
        );
        return (
            <div className={`${aspect} ${className} rounded-xl overflow-hidden shadow-lg`}>
                <img src={img} alt={item.title || ''} className="w-full h-full object-cover" loading="lazy"/>
            </div>
        );
    };

    // ── Split variant (default) ──
    if (variant === 'split') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={sectionRef} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>

                    <div className="relative">
                        {items.map((item, i) => (
                            <div key={i}
                                className="transition-all duration-500"
                                style={{
                                    opacity: i === currentSlide ? 1 : 0,
                                    transform: i === currentSlide ? 'translateX(0)' : i < currentSlide ? 'translateX(-40px)' : 'translateX(40px)',
                                    position: i === currentSlide ? 'relative' : 'absolute',
                                    top: 0, left: 0, right: 0,
                                    pointerEvents: i === currentSlide ? 'auto' : 'none',
                                }}>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                                    {/* Text content */}
                                    <div className="order-2 lg:order-1">
                                        <div className="flex items-center gap-3 mb-4">
                                            {item.icon && (
                                                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                                    <SectionIcon name={item.icon} size={20} color={s.accent}/>
                                                </div>
                                            )}
                                            <h3 className="text-xl md:text-2xl font-bold" style={{ color: s.text }}>{item.title}</h3>
                                        </div>
                                        {item.description && (
                                            <p className="text-base leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>
                                        )}
                                        <FeatureList features={item.features || []}/>
                                        <CtaButton item={item}/>
                                    </div>

                                    {/* Image */}
                                    <div className="order-1 lg:order-2">
                                        <SlideImage item={item} aspect="aspect-[16/10]"/>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Navigation */}
                    {total > 1 && (
                        <div className="flex items-center justify-between mt-8">
                            <Dots/>
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-medium" style={{ color: s.textSub }}>{currentSlide + 1} / {total}</span>
                                <ArrowBtn direction="prev" onClick={prev}/>
                                <ArrowBtn direction="next" onClick={next}/>
                            </div>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Stacked variant ──
    if (variant === 'stacked') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={sectionRef} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>

                    <div className="relative max-w-4xl mx-auto">
                        {/* Side arrows */}
                        {total > 1 && (
                            <>
                                <div className="hidden md:block absolute -left-14 top-1/2 -translate-y-1/2 z-10">
                                    <ArrowBtn direction="prev" onClick={prev}/>
                                </div>
                                <div className="hidden md:block absolute -right-14 top-1/2 -translate-y-1/2 z-10">
                                    <ArrowBtn direction="next" onClick={next}/>
                                </div>
                            </>
                        )}

                        {items.map((item, i) => (
                            <div key={i}
                                className="transition-all duration-500"
                                style={{
                                    opacity: i === currentSlide ? 1 : 0,
                                    transform: i === currentSlide ? 'translateX(0)' : i < currentSlide ? 'translateX(-40px)' : 'translateX(40px)',
                                    position: i === currentSlide ? 'relative' : 'absolute',
                                    top: 0, left: 0, right: 0,
                                    pointerEvents: i === currentSlide ? 'auto' : 'none',
                                }}>
                                {/* Image on top */}
                                <SlideImage item={item} aspect="aspect-[16/9]"/>

                                {/* Content below */}
                                <div className="text-center mt-8">
                                    <div className="flex items-center justify-center gap-3 mb-3">
                                        {item.icon && (
                                            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                                <SectionIcon name={item.icon} size={20} color={s.accent}/>
                                            </div>
                                        )}
                                        <h3 className="text-xl md:text-2xl font-bold" style={{ color: s.text }}>{item.title}</h3>
                                    </div>
                                    {item.description && (
                                        <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: s.textSub }}>{item.description}</p>
                                    )}
                                    {(item.features || []).length > 0 && (
                                        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4">
                                            {(item.features || []).map((feat, fi) => (
                                                <span key={fi} className="inline-flex items-center gap-1.5 text-sm" style={{ color: s.textSub }}>
                                                    <Check/> {feat}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-5">
                                        <CtaButton item={item}/>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Mobile arrows + dots */}
                        {total > 1 && (
                            <div className="flex items-center justify-center gap-4 mt-8">
                                <div className="md:hidden"><ArrowBtn direction="prev" onClick={prev}/></div>
                                <Dots/>
                                <div className="md:hidden"><ArrowBtn direction="next" onClick={next}/></div>
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Minimal variant ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
            <div ref={sectionRef} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s}/>

                <div className="max-w-3xl mx-auto">
                    <div className="relative">
                        {items.map((item, i) => {
                            const img = resolveImage(item, 'image', mediaFolders);
                            return (
                                <div key={i}
                                    className="transition-all duration-500"
                                    style={{
                                        opacity: i === currentSlide ? 1 : 0,
                                        transform: i === currentSlide ? 'translateY(0)' : 'translateY(20px)',
                                        position: i === currentSlide ? 'relative' : 'absolute',
                                        top: 0, left: 0, right: 0,
                                        pointerEvents: i === currentSlide ? 'auto' : 'none',
                                    }}>
                                    <div className="flex items-center gap-5 p-5 rounded-xl transition-all duration-300"
                                        style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : hexToRgba(s.accent, 0.04) }}>
                                        {/* Small thumbnail */}
                                        <div className="shrink-0">
                                            {img ? (
                                                <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden shadow-md">
                                                    <img src={img} alt={item.title || ''} className="w-full h-full object-cover" loading="lazy"/>
                                                </div>
                                            ) : item.icon ? (
                                                <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.1) }}>
                                                    <SectionIcon name={item.icon} size={36} color={s.accent}/>
                                                </div>
                                            ) : (
                                                <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                                    <svg className="w-10 h-10" style={{ color: hexToRgba(s.accent, 0.3) }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <rect x="2" y="2" width="20" height="20" rx="2" strokeWidth={1.5}/>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>

                                        {/* Title + 3 features */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{item.title}</h3>
                                            <FeatureList features={item.features || []} limit={3}/>
                                            {item.cta_text && (
                                                <a {...resolveLinkProps(item.cta_link || '#')}
                                                    className="inline-flex items-center gap-1 text-sm font-medium mt-3 hover:underline transition-colors"
                                                    style={{ color: s.accent }}>
                                                    {item.cta_text}
                                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Navigation */}
                    {total > 1 && (
                        <div className="flex items-center justify-center gap-4 mt-6">
                            <ArrowBtn direction="prev" onClick={prev}/>
                            <Dots/>
                            <ArrowBtn direction="next" onClick={next}/>
                        </div>
                    )}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
        </section>
    );
}
